/**
 * PromoTemplates.jsx — Shared promotion template system
 * Used by PromoBanner (popup), Promotions page (cards), and Admin preview.
 *
 * Five templates:
 *   flash    — Bold gradient, giant badge, urgency
 *   premium  — Dark/gold, elegant, upmarket
 *   seasonal — Warm earthy, product image hero
 *   clean    — White card, teal accent, minimal
 *   bold     — Split layout, massive discount, punchy
 */

import { useState, useEffect } from 'react'
import {
  X, Tag, Sparkles, ChevronRight, Clock, Gift,
  Zap, Star, Flame, Leaf,
} from 'lucide-react'

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function discountBadge(p) {
  if (!p) return 'SPECIAL OFFER'
  if (p.discount_type === 'percentage') return `${p.discount_value || 0}% OFF`
  if (p.discount_type === 'fixed') return `R${p.discount_value || 0} OFF`
  if (p.discount_type === 'free_shipping') return 'FREE DELIVERY'
  return 'SPECIAL OFFER'
}

export function timeLeft(ends_at) {
  if (!ends_at) return null
  const diff = new Date(ends_at) - new Date()
  if (diff <= 0) return 'Expired'
  const days = Math.floor(diff / 86400000)
  const hrs  = Math.floor((diff % 86400000) / 3600000)
  const mins = Math.floor((diff % 3600000) / 60000)
  if (days > 0) return `${days}d ${hrs}h left`
  if (hrs > 0)  return `${hrs}h ${mins}m left`
  return `${mins}m left`
}

// Template meta — used for picker UI
export const TEMPLATES = [
  {
    id: 'flash',
    label: 'Flash Sale',
    icon: Zap,
    desc: 'Bold gradient, urgent energy — perfect for time-limited deals',
    preview_gradient: 'linear-gradient(135deg, #dc2626, #b91c1c)',
  },
  {
    id: 'premium',
    label: 'Premium',
    icon: Star,
    desc: 'Dark & gold — upmarket feel for wine, spirits, high-end products',
    preview_gradient: 'linear-gradient(135deg, #1a1a1a, #2d2416)',
  },
  {
    id: 'seasonal',
    label: 'Seasonal',
    icon: Leaf,
    desc: 'Warm earthy tones — Heritage Day, Christmas, braai season specials',
    preview_gradient: 'linear-gradient(135deg, #92400e, #78350f)',
  },
  {
    id: 'clean',
    label: 'Clean',
    icon: Tag,
    desc: 'White card, teal accent — minimal and very readable, great for promo codes',
    preview_gradient: 'linear-gradient(135deg, #0e947a, #0a6b58)',
  },
  {
    id: 'bold',
    label: 'Bold',
    icon: Flame,
    desc: 'Split layout, massive numbers — clearance, big discounts, no-nonsense',
    preview_gradient: 'linear-gradient(135deg, #1d4ed8, #1e3a8a)',
  },
]

// Template colour configs
const TEMPLATE_STYLES = {
  flash: {
    bg: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 60%, #7f1d1d 100%)',
    badgeBg: 'rgba(255,255,255,0.2)',
    badgeText: '#ffffff',
    titleColor: '#ffffff',
    textColor: 'rgba(255,255,255,0.85)',
    codeBg: 'rgba(0,0,0,0.25)',
    codeBorder: 'rgba(255,255,255,0.2)',
    codeText: '#fff',
    ctaBg: '#ffffff',
    ctaText: '#991b1b',
    dimColor: 'rgba(255,255,255,0.6)',
  },
  premium: {
    bg: 'linear-gradient(135deg, #1a1a1a 0%, #2d2416 100%)',
    badgeBg: 'rgba(212,175,55,0.2)',
    badgeText: '#d4af37',
    titleColor: '#d4af37',
    textColor: 'rgba(255,255,255,0.75)',
    codeBg: 'rgba(212,175,55,0.1)',
    codeBorder: 'rgba(212,175,55,0.3)',
    codeText: '#d4af37',
    ctaBg: '#d4af37',
    ctaText: '#1a1a1a',
    dimColor: 'rgba(255,255,255,0.5)',
  },
  seasonal: {
    bg: 'linear-gradient(135deg, #92400e 0%, #78350f 50%, #451a03 100%)',
    badgeBg: 'rgba(255,237,213,0.2)',
    badgeText: '#fed7aa',
    titleColor: '#ffffff',
    textColor: 'rgba(254,215,170,0.9)',
    codeBg: 'rgba(0,0,0,0.2)',
    codeBorder: 'rgba(254,215,170,0.3)',
    codeText: '#fed7aa',
    ctaBg: '#fed7aa',
    ctaText: '#7c2d12',
    dimColor: 'rgba(254,215,170,0.6)',
  },
  clean: {
    bg: '#ffffff',
    badgeBg: 'rgba(14,148,122,0.1)',
    badgeText: '#0e947a',
    titleColor: '#111827',
    textColor: '#4b5563',
    codeBg: 'rgba(14,148,122,0.06)',
    codeBorder: 'rgba(14,148,122,0.2)',
    codeText: '#0e947a',
    ctaBg: '#0e947a',
    ctaText: '#ffffff',
    dimColor: '#6b7280',
    topBar: '#0e947a',   // coloured stripe at top
    border: '#e5e7eb',
  },
  bold: {
    bg: 'linear-gradient(135deg, #1d4ed8 0%, #1e3a8a 100%)',
    badgeBg: 'rgba(255,255,255,0.15)',
    badgeText: '#ffffff',
    titleColor: '#ffffff',
    textColor: 'rgba(255,255,255,0.8)',
    codeBg: 'rgba(0,0,0,0.2)',
    codeBorder: 'rgba(255,255,255,0.2)',
    codeText: '#bfdbfe',
    ctaBg: '#ffffff',
    ctaText: '#1e3a8a',
    dimColor: 'rgba(255,255,255,0.6)',
  },
}

function getStyle(template, accentColor) {
  const base = TEMPLATE_STYLES[template] || TEMPLATE_STYLES.clean
  if (!accentColor) return base
  // Override primary colour with admin's custom hex
  return {
    ...base,
    ctaBg: accentColor,
    badgeText: template === 'clean' ? accentColor : base.badgeText,
    titleColor: template === 'clean' ? '#111827' : accentColor,
    topBar: accentColor,
  }
}

// ─── Countdown timer ──────────────────────────────────────────────────────────

function Countdown({ ends_at, color = 'rgba(255,255,255,0.7)', size = 'text-xs' }) {
  const [label, setLabel] = useState(timeLeft(ends_at))
  useEffect(() => {
    if (!ends_at) return
    const t = setInterval(() => setLabel(timeLeft(ends_at)), 30000)
    return () => clearInterval(t)
  }, [ends_at])
  if (!label) return null
  return (
    <span className={`flex items-center gap-1 ${size}`} style={{ color }}>
      <Clock size={11}/> {label}
    </span>
  )
}

// ─── PromoPopupCard — the main popup card rendered by PromoBanner ─────────────

export function PromoPopupCard({ promo, onDismiss, onCTA, compact = false }) {
  const template = promo.template || 'clean'
  const s = getStyle(template, promo.accent_color)
  const badge = discountBadge(promo)
  const pad = compact ? 'p-3' : 'p-4'

  return (
    <div
      className={`rounded-2xl overflow-hidden shadow-2xl relative ${template === 'clean' ? 'border' : ''}`}
      style={{
        background: s.bg,
        borderColor: s.border || 'transparent',
      }}
    >
      {/* Top accent bar for clean template */}
      {s.topBar && (
        <div className="h-1.5 w-full" style={{ background: s.topBar }}/>
      )}

      {/* Dismiss */}
      <button
        onClick={() => onDismiss(promo.id)}
        className="absolute top-3 right-3 p-1 rounded-full hover:bg-black/10 transition-colors"
        style={{ color: s.dimColor }}
      >
        <X size={14}/>
      </button>

      <div className={pad}>
        {promo.banner_image && (
          <div className="flex justify-center mb-3">
            <img src={promo.banner_image} alt="" className="rounded-xl object-contain max-h-40"
              style={{ width: `${promo.image_scale || 100}%`, maxWidth: '100%' }}/>
          </div>
        )}
        {/* Badge row */}
        <div className="flex items-center gap-2 mb-2">
          <span
            className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full tracking-wide"
            style={{ background: s.badgeBg, color: s.badgeText }}
          >
            {template === 'flash'    && <Zap size={10}/>}
            {template === 'premium'  && <Star size={10}/>}
            {template === 'seasonal' && <Leaf size={10}/>}
            {template === 'clean'    && <Tag size={10}/>}
            {template === 'bold'     && <Flame size={10}/>}
            {badge}
          </span>
          <Countdown ends_at={promo.ends_at} color={s.dimColor}/>
        </div>

        {/* Bold template: split layout — big number on left */}
        {template === 'bold' && promo.discount_type === 'percentage' ? (
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-shrink-0">
              <span className="text-5xl font-black leading-none" style={{ color: '#ffffff' }}>
                {promo.discount_value}
              </span>
              <span className="text-xl font-bold" style={{ color: 'rgba(255,255,255,0.7)' }}>%</span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-base leading-tight" style={{ color: s.titleColor }}>
                {promo.title}
              </h3>
              {promo.description && (
                <p className="text-xs mt-0.5 line-clamp-2" style={{ color: s.textColor }}>
                  {promo.description}
                </p>
              )}
            </div>
          </div>
        ) : (
          <>
            <h3 className={`font-bold ${compact ? 'text-sm' : 'text-base'} leading-tight mb-1`}
                style={{ color: s.titleColor }}>
              {promo.title}
            </h3>
            {promo.description && !compact && (
              <p className="text-xs mb-2.5 line-clamp-2" style={{ color: s.textColor }}>
                {promo.description}
              </p>
            )}
          </>
        )}

        {/* CTA */}
        {promo.cta_label && (
          <button
            onClick={() => onCTA(promo)}
            className="w-full py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5 transition-opacity hover:opacity-90"
            style={{ background: s.ctaBg, color: s.ctaText }}
          >
            {promo.cta_label} <ChevronRight size={14}/>
          </button>
        )}
      </div>
    </div>
  )
}

// ─── PromoBannerStrip — the slide-down top strip ──────────────────────────────

export function PromoBannerStrip({ promo, onDismiss, onCTA }) {
  const template = promo.template || 'clean'
  const s = getStyle(template, promo.accent_color)
  const badge = discountBadge(promo)

  return (
    <div
      className="flex items-center gap-2 px-4 py-2.5 shadow-lg"
      style={{ background: s.bg }}
    >
      {/* Badge */}
      <span
        className="flex items-center gap-1 text-xs font-bold px-2.5 py-0.5 rounded-full tracking-wide flex-shrink-0"
        style={{ background: s.badgeBg, color: s.badgeText }}
      >
        <Sparkles size={9}/> {badge}
      </span>

      {promo.banner_image && (
        <img src={promo.banner_image} alt="" className="w-7 h-7 rounded object-cover flex-shrink-0 border border-white/20"/>
      )}
      {/* Title */}
      <span className="flex-1 font-semibold text-sm truncate" style={{ color: s.titleColor }}>
        {promo.title}
      </span>

      {/* Countdown */}
      {promo.ends_at && (
        <Countdown ends_at={promo.ends_at} color={s.dimColor}/>
      )}

      {/* CTA */}
      {promo.cta_label && (
        <button
          onClick={() => onCTA(promo)}
          className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg flex-shrink-0 transition-opacity hover:opacity-80"
          style={{ background: s.ctaBg, color: s.ctaText }}
        >
          {promo.cta_label} <ChevronRight size={10}/>
        </button>
      )}

      {/* Dismiss */}
      <button onClick={() => onDismiss(promo.id)} style={{ color: s.dimColor }} className="flex-shrink-0">
        <X size={15}/>
      </button>
    </div>
  )
}

// ─── PromoPageCard — full card for the Promotions listing page ────────────────

export function PromoPageCard({ promo, index, onDismiss, onCTA, onCopy }) {
  const template = promo.template || 'clean'
  const s = getStyle(template, promo.accent_color)
  const badge = discountBadge(promo)
  return (
    <div
      className={`relative rounded-2xl overflow-hidden shadow-lg ${template === 'clean' ? 'border' : ''} ${!promo.read ? 'ring-2 ring-white/20' : ''}`}
      style={{ background: s.bg, borderColor: s.border || 'transparent' }}
    >
      {s.topBar && <div className="h-1.5" style={{ background: s.topBar }}/>}

      {/* Unread pulse */}
      {!promo.read && (
        <div className="absolute top-4 left-4 w-2 h-2 rounded-full animate-pulse"
             style={{ background: s.badgeText }}/>
      )}

      {/* Dismiss */}
      <button onClick={() => onDismiss(promo.id)}
        className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-black/10 transition-colors"
        style={{ color: s.dimColor }}>
        <X size={14}/>
      </button>

      <div className="p-5">
        {promo.banner_image && (
          <div className="flex justify-center mb-3">
            <img src={promo.banner_image} alt="" className="rounded-xl object-contain max-h-40"
              style={{ width: `${promo.image_scale || 100}%`, maxWidth: '100%' }}/>
          </div>
        )}
        {/* Badge + timer row */}
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full tracking-wide"
                style={{ background: s.badgeBg, color: s.badgeText }}>
            {template === 'flash'    && <Zap size={10}/>}
            {template === 'premium'  && <Star size={10}/>}
            {template === 'seasonal' && <Leaf size={10}/>}
            {template === 'clean'    && <Tag size={10}/>}
            {template === 'bold'     && <Flame size={10}/>}
            {badge}
          </span>
          <Countdown ends_at={promo.ends_at} color={s.dimColor} size="text-xs"/>
        </div>

        {/* Bold split */}
        {template === 'bold' && promo.discount_type === 'percentage' ? (
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-shrink-0 text-center">
              <span className="text-6xl font-black leading-none" style={{ color: '#ffffff' }}>
                {promo.discount_value}
              </span>
              <span className="text-2xl font-bold" style={{ color: 'rgba(255,255,255,0.6)' }}>%</span>
            </div>
            <div>
              <h3 className="font-bold text-xl leading-tight" style={{ color: s.titleColor }}>{promo.title}</h3>
              {promo.description && (
                <p className="text-sm mt-1" style={{ color: s.textColor }}>{promo.description}</p>
              )}
            </div>
          </div>
        ) : (
          <>
            <h3 className="font-bold text-xl leading-tight mb-1.5" style={{ color: s.titleColor }}>
              {promo.title}
            </h3>
            {promo.description && (
              <p className="text-sm mb-3" style={{ color: s.textColor }}>{promo.description}</p>
            )}
          </>
        )}

        {promo.min_order_value > 0 && (
          <p className="text-xs mb-3" style={{ color: s.dimColor }}>
            Min order: R{parseFloat(promo.min_order_value).toFixed(0)}
          </p>
        )}

        {promo.cta_label && promo.cta_link && (
          <button onClick={() => onCTA(promo)}
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl font-semibold text-sm transition-opacity hover:opacity-90"
            style={{ background: s.ctaBg, color: s.ctaText }}>
            {promo.cta_label} <ChevronRight size={14}/>
          </button>
        )}
      </div>
    </div>
  )
}

// ─── Admin Preview Card ───────────────────────────────────────────────────────

export function AdminPromoPreview({ promo, mode = 'popup' }) {
  const mockDismiss = () => {}
  const mockCTA    = () => {}

  if (mode === 'banner') {
    return (
      <div className="rounded-xl overflow-hidden">
        <PromoBannerStrip promo={promo} onDismiss={mockDismiss} onCTA={mockCTA}/>
      </div>
    )
  }

  if (mode === 'card') {
    return (
      <div className="max-w-sm">
        <PromoPageCard
          promo={promo}
          index={0}
          onDismiss={mockDismiss}
          onCTA={mockCTA}
        />
      </div>
    )
  }

  // popup mode
  return (
    <div className="max-w-xs">
      <PromoPopupCard promo={promo} onDismiss={mockDismiss} onCTA={mockCTA}/>
    </div>
  )
}
